import type { User, UserReference } from '@core/account-types';
import type { Attachment } from '@core/attachments-types';
import type { GdprConsentRequestKind, GdprStatus } from '@core/gdpr-types';
import type { APIFileStatusValues } from '@core/http-types';
import type { QuestionKind, QuestionOptions } from '@core/interview-templates-types';
import type { TOpenQuestionKind } from '@core/open-questions-templates-types';
import type { Placement } from '@core/placements-types';
import type { Field } from '@core/profile-fields-types';
import type { CandidateReferrer, TReferralQuestionKind } from '@core/referrals-types';
import { GeneralTitleValue, ProfessionalTitleValue } from '@core/salutation-fields-types';
import type { Visibility } from '@core/visibility-types';

export type OpenQuestionAnswer = {
  id: number;
  kind: TOpenQuestionKind | TReferralQuestionKind | QuestionKind;
  flag?: boolean;
  content: string | null;
  contents?: string[];
  question: string;
  attachment?: null;
  attachments?: Attachment[];
  questionOptions?: QuestionOptions;
  visibility?: Visibility;
  visible?: boolean;
};

export interface Referrer {
  avatarThumbUrl: string;
  firstName: string;
  lastName: string;
  anonymizedAt: string | null;
  hasAvatar?: boolean;
  initials?: string;
}

export interface GroupedOpenQuestionAnswer {
  openQuestionAnswers: OpenQuestionAnswer[];
  updatedAt?: string;
  createdAt?: string;
  offer: null | { id: number | null; status: string | null; title: string };
  user?: Referrer;
}

export type CandidateCustomField = {
  id: number;
  label: string;
  position: number;
  values: string[];
};

type CandidateGroupedOpenQuestionAnswer = {
  createdAt: string;
  offer: {
    id: number | null;
    status: string | null;
    title: string;
  } | null;
  openQuestionAnswers: OpenQuestionAnswer[];
};

type CandidateOpenQuestionAnswer = {
  content: string;
  id: number;
  kind: TOpenQuestionKind;
  question: string;
};

export type CandidateSourcingData = {
  url: string;
  workHistory?: Array<{ title: string; company: string; duration: string }>;
  educationHistory?: Array<{ school: string; degree: string; duration: string }>;
  about?: string;
  website?: string;
  location?: string;
  jobTitle?: string;
  worksFor?: string;
  roles?: string;
  skills?: string;
  projectViews?: string;
  appreciations?: string;
  followers?: string;
  following?: string;
  shots?: string;
  likes?: string;
  starred?: string;
  reputation?: string;
  answers?: string;
  questions?: string;
  peopleReached?: string;
  tweets?: string;
  haves?: string;
  wants?: string;
};

export interface Candidate {
  admin: User;
  adminId: number;
  adminappUrl: string;
  createdAt: string;
  deletedAt?: string;
  emails: string[];
  followed: boolean;
  hasAvatar: boolean;
  id: number;
  initials: string;
  myLastRating: string | null;
  myPendingResultRequest: boolean;
  myUpcomingEvent: boolean;
  name: string;
  newMessages: boolean;
  notesCount: number;
  pendingResultRequest: boolean;
  phones: string[];
  photoThumbUrl: string;
  placements: Placement[];
  positiveRatings: number;
  rating: number;
  ratings: { yes: number; strongYes: number; no: number; neutral: number };
  ratingsCount: number;
  ratingVisible: boolean;
  referrer: null | string;
  reviewed: boolean;
  source: SourcesType;
  tasksCount: number;
  unreadNotifications: boolean;
  upcomingCall: boolean;
  upcomingEvent: boolean;
  upcomingEventKind: boolean;
  upcomingMeeting: boolean;
  updatedAt: string;
  viewed: boolean;
  isAnonymous: boolean;
  isRevealed: boolean;
  everRevealedForIds: number[];
  everRevealedFor: User[];
  salutation: GeneralTitleValue | null;
  title: ProfessionalTitleValue | null;
}

export interface CandidateReference extends Candidate {
  type: 'Candidate';
}

export interface CandidateAsyncJob {
  id: number;
  hasAvatar: boolean;
  emails: string[];
  initials: string;
  name: string;
  photoThumbUrl: string;
}

export interface FullCandidate extends Candidate {
  adminIds: number[];
  admins: UserReference[];
  allRatingsCount: number;
  ratingsWithoutPlacementCount: number;
  attachmentsCount: number;
  coverLetter: null | string;
  coverLetterFileOriginalUrl?: null | string;
  coverLetterFileProcessingStatus?: APIFileStatusValues;
  coverLetterFileUrl?: null | string;
  hasCoverLetter?: boolean;
  customFields: CandidateCustomField[];
  cvOriginalUrl: null | string;
  cvProcessingStatus: null | APIFileStatusValues;
  cvUrl: null | string;
  duplicates: number[];
  example?: boolean;
  eventsCount: number;
  fields: Field[];
  gdprExpiresAt: string;
  gdprConsentRequestSentAt: string;
  gdprConsentRequestCompletedAt: string | null;
  gdprStatus: GdprStatus;
  gdprConsentRequestType: GdprConsentRequestKind;
  gdprConsentEverGiven: null | boolean;
  gdprScheduledToDeleteAt: string | null;
  groupedOpenQuestionAnswers: CandidateGroupedOpenQuestionAnswer[];
  inActiveShare: boolean;
  lastActivityAt: string;
  links: string[];
  mailboxMessagesCount: number;
  messagesCount: number;
  textingMessagesCount: number;
  onlineData: null;
  openQuestionAnswers: CandidateOpenQuestionAnswer[] | OpenQuestionAnswer[];
  pendingRequestLink: boolean;
  photoUrl: string;
  referralReferrers: CandidateReferrer[];
  referralReferrersIds: number[];
  socialLinks: string[];
  sources: string[];
  sourcingData: CandidateSourcingData;
  sourcingOrigin: null | string;
  tags: string[];
  deletedAt: string;
  deletedByKind: string;
  isHired: boolean;
  hiredById?: number;
  hiredOfferId?: number;
  hiredOfferTitle?: string;
  hiredDepartmentName?: string;
  hiredDepartmentId?: number;
  linkedAdminId?: number;
  linkedAdmin?: UserReference;
  linkedinJobApplicationId: string | null;
  upcomingEventMine: boolean;
  upcomingEventStartsAt: string;
  sharedContainerCount: number;
  sharedAdminCount: number;
  invalidEmails: string[];
}

export type CandidateRating = {
  id: number;
  ratingVisible: boolean;
  positiveRatings: number;
  ratings: { yes: number; strongYes: number; no: number; neutral: number };
};

export enum SourcesType {
  CareerSite = 'career_site',
  Email = 'email',
  Manual = 'manual',
  Import = 'import',
  Referral = 'referral',
}

export interface ShareCandidateWithTeamResponse {
  candidate: { sharedAdminCount: number; sharedContainerCount: number };
}

export interface ShareOnTheGoRequestPayload {
  grantAccess: Array<{
    adminId: number;
    placementId: number | null;
    offerId: number | null;
    talentPoolId: number | null;
  }>;
}
